import type { MutationOptions, QueryObserverOptions } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  getUserColumnPreferences,
  getUserProfile,
  putUserProfile,
  uploadUserColumnPreferences,
} from 'api/user';
import type {
  GetUserProfileResponseDTO,
  UpdateUserRequestBodyDTO,
  UploadUserColumnPreferences,
} from 'declarations/dto/user';
import { RequestQueryKeysE, RequestStaleTimeE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';

type UseUpdateUserParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, UpdateUserRequestBodyDTO>['onSuccess'];
};

type UseFetchUserPreferencesParamsT = {
  onSuccess: QueryObserverOptions<string, ApiErrorI>['onSuccess'];
  onError: QueryObserverOptions<string, ApiErrorI>['onError'];
};

export const useFetchUser = (
  idToken: string | null
): ReturnType<typeof useQuery<GetUserProfileResponseDTO, ApiErrorI>> =>
  useQuery<GetUserProfileResponseDTO, ApiErrorI>([RequestQueryKeysE.FetchUser], getUserProfile, {
    enabled: !!idToken,
    staleTime: RequestStaleTimeE.UserProfile,
  });

export const useUpdateUser = ({
  onSuccess,
}: UseUpdateUserParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, UpdateUserRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, UpdateUserRequestBodyDTO>(
    [RequestQueryKeysE.UpdateUser],
    (userProfileData): Promise<void> => putUserProfile(userProfileData),
    { onSuccess }
  );

export const useFetchUserColumnPreferences = ({
  onSuccess,
}: UseFetchUserPreferencesParamsT): ReturnType<typeof useQuery<string, ApiErrorI>> => {
  return useQuery<string, ApiErrorI>(
    [RequestQueryKeysE.FetchUserColumnPreferences],
    getUserColumnPreferences,
    { onSuccess, enabled: !localStorage.getItem('visibleColumns') }
  );
};

export const useUpdateUserColumnPreferences = (): ReturnType<
  typeof useMutation<void, ApiErrorI, UploadUserColumnPreferences>
> =>
  useMutation<void, ApiErrorI, UploadUserColumnPreferences>(
    [RequestQueryKeysE.UpdateUserColumnPreferences],
    (userColumnPreferencesData): Promise<void> =>
      uploadUserColumnPreferences(userColumnPreferencesData)
  );
